/**
 * Яндекс.Метрика.
 *
 * igra.now — счётчик грузится ВСЕГДА, независимо от плашки cookie (решение
 * оператора 2026-09-29: за тумблером статистика стояла пустой).
 *
 * Сборки с `VITE_METRIKA_CONSENT=1` (i5g.org, `build:i5g`) — по-прежнему
 * HK-IGRA.188: без согласия «Аналитика» скрипт НЕ ГРУЗИТСЯ. Не «грузится,
 * но не отправляет», не «грузится обезличенно» — не грузится вовсе, потому что
 * сам факт загрузки чужого скрипта это уже передача данных (IP, User-Agent,
 * адрес страницы) третьему лицу. Отозвали согласие — счётчик выключается в той
 * же сессии, не дожидаясь перезагрузки.
 *
 * Номер счётчика приходит переменной окружения (`VITE_YANDEX_METRIKA_ID`). Его
 * нет — не грузим ничего и молчим: в разработке и в тестах счётчика быть не
 * должно, а зашитый в код номер однажды уехал бы в чужую сборку.
 *
 * События — воронка покупки и воронка общей подписки (IGRA-110 §10). Ни
 * одного личного поля: ни email, ни id, ни суммы конкретного человека — этого
 * достаточно, чтобы считать конверсию, и недостаточно, чтобы узнать человека.
 */
import { hasConsent, onConsentChange } from './consent'

export type CheckoutFunnelEvent =
  /** Показ лендинга. Параметр `variant` — A/B/C, иначе замер бессмыслен. */
  | 'landing_view'
  /** Клик по главной кнопке лендинга. */
  | 'landing_cta'
  /** Выбор тарифа на витрине покупки. */
  | 'checkout_period'
  /** Отправка формы покупки (счёт создаётся). */
  | 'checkout_submit'
  /** Оплата, ПОДТВЕРЖДЁННАЯ бэкендом. */
  | 'checkout_success'

/**
 * IGRA-110 §10 — воронка «Поделиться подпиской»: приглашение → открыто →
 * принято, плюс действия участника. Имена перечислены списком, а не собираются
 * строками по экранам: набор событий это контракт с аналитикой, и опечатка в
 * нём тихо обнуляет отчёт.
 *
 * Свойство `role` кладёт `trackPoolEvent`
 * (`modules/vpn/utils/poolAnalytics.ts`): без роли ни одно из этих чисел не
 * читается — владелец и участник видят разные экраны.
 */
export const POOL_ANALYTICS_EVENTS = [
  'pool_tile_view',
  'pool_page_view',
  'pool_invite_sent',
  'pool_invite_link_shared',
  'pool_invite_revoked',
  'pool_invite_opened',
  'pool_invite_accept_blocked',
  'pool_invite_accepted',
  'pool_member_device_offer_view',
  'pool_member_device_purchase',
  'pool_leave_view',
  'pool_leave_buy_own',
  'pool_leave_plain',
  'pool_kick',
  'pool_host_expired_notice',
  'pool_renew_by_member',
] as const

export type PoolAnalyticsEvent = (typeof POOL_ANALYTICS_EVENTS)[number]

/**
 * IGRA-387 — судьба письма с кодом входа. Параметр `status` — то, что ответила
 * ручка доставки, `channel_chosen` — какой запасной канал выбрал человек.
 * Ни адреса, ни кода, ни домена почты: доля отказов считается и без них.
 */
export type OtpDeliveryAnalyticsEvent = 'otp_delivery_status'

/**
 * IGRA-TEAM — воронка лендинга `/team` для сетевых лидеров.
 *
 * Свои имена целей, а НЕ `landing_view`/`landing_cta` с ещё одним значением
 * `variant`. Теми двумя целями считается сплит-тест A против B, и всякий, кто
 * смотрит на их итог без фильтра по варианту, получил бы сумму с посторонней
 * страницей. Прецедент тот же, что был у `/adzero` (IGRA-206): лендинг вне жребия
 * считается отдельно, потому что смешать нечего только тогда, когда смешивать
 * негде.
 */
export type TeamLandingAnalyticsEvent = 'team_landing_view' | 'team_landing_cta'

/**
 * sprint-17 — воронка скачивания на i5g.org (свой счётчик 113170864, номер
 * задаёт скрипт `build:i5g`). Параметр `platform` — mac-arm / mac-intel /
 * windows, личных полей нет.
 */
export type I5gAnalyticsEvent =
  /** Клик «Скачать» на лендинге (до формы входа или перехода). */
  | 'i5g_download_click'
  /** Страница скачивания сама запустила сборку под систему. */
  | 'i5g_download_start'
  /** Компьютер, но сборки под его систему в выпуске нет. */
  | 'i5g_download_nobuild'
  /** Страницу скачивания открыли с телефона. */
  | 'i5g_download_mobile'

export type AnalyticsEvent =
  | CheckoutFunnelEvent
  | PoolAnalyticsEvent
  | OtpDeliveryAnalyticsEvent
  | TeamLandingAnalyticsEvent
  | I5gAnalyticsEvent

type MetrikaFn = ((id: number, action: string, ...args: unknown[]) => void) & { a?: unknown[] }

declare global {
  interface Window {
    ym?: MetrikaFn
  }
}

const SCRIPT_ID = 'igra-ym'
const SCRIPT_SRC = 'https://mc.yandex.ru/metrika/tag.js'

/** Сборка держит счётчик за тумблером согласия (i5g.org). */
function needsConsent(): boolean {
  return import.meta.env.VITE_METRIKA_CONSENT === '1'
}

/** Счётчик поднят в этой вкладке. */
let started = false

export function metrikaId(): number | null {
  const raw = import.meta.env.VITE_YANDEX_METRIKA_ID
  const id = Number(raw)

  return Number.isInteger(id) && id > 0 ? id : null
}

/**
 * Загрузить счётчик. Вызывается ТОЛЬКО когда согласие уже есть — проверку
 * держит `setupAnalytics()`, здесь она вторым рубежом, потому что функция
 * экспортируется и однажды будет вызвана откуда-то ещё.
 */
function start(id: number): void {
  if (started || typeof document === 'undefined') return
  if (needsConsent() && !hasConsent('analytics')) return
  started = true

  // Официальный сниппет Метрики в читаемом виде: очередь вызовов до загрузки
  // скрипта, затем сам скрипт.
  const queued: MetrikaFn = ((...args: unknown[]) => {
    ;(queued.a = queued.a || []).push(args)
  }) as MetrikaFn
  window.ym = window.ym ?? queued

  const script = document.createElement('script')
  script.id = SCRIPT_ID
  script.async = true
  // `?id=` обязателен: с `ssr: true` tag.js без него очередь не разбирает и
  // счётчик молча не стартует (проверено живьём, sprint-17 Task 11).
  script.src = `${SCRIPT_SRC}?id=${id}`
  document.head.appendChild(script)

  window.ym?.(id, 'init', {
    // HK-IGRA.203 — конфигурация счётчика по решению владельца. webvisor и
    // clickmap включены сознательно: они пишут движения курсора и содержимое
    // полей, поэтому и держатся строго за тумблером согласия «Аналитика» —
    // без него start() сюда не доходит. Ссылка-ключ и поля ввода почты закрыты
    // от записи в самой разметке — `ym-hide-content` и `ym-disable-keys`,
    // сторож в tests/unit/metrikaWebvisorMasking.test.ts.
    ssr: true,
    webvisor: true,
    clickmap: true,
    ecommerce: 'dataLayer',
    trackLinks: true,
    accurateTrackBounce: true,
  })
}

/**
 * Куки Метрики. `_ym_uid` — идентификатор посетителя, `_ym_d` — дата первого
 * визита, `_ym_isad` — признак блокировщика рекламы, `_ym_visorc` — кука
 * вебвизора (живьём, sprint-17, она переживала отзыв). Отзыв согласия обязан
 * унести и их: оставленный `_ym_uid` узнает человека при следующем визите.
 */
const METRIKA_COOKIES = ['_ym_uid', '_ym_d', '_ym_isad', '_ym_visorc']

/**
 * Кука удаляется только тем же `path`/`domain`, каким поставлена. Метрика
 * ставит свои на `path=/` и на домен с точкой (`.igra.now`), поэтому чистим
 * все три варианта: без домена, на текущем хосте и на `.<хост>`. Лишние
 * присваивания браузер молча игнорирует — это дешевле, чем гадать.
 */
function clearMetrikaCookies(): void {
  const host = typeof window === 'undefined' ? '' : window.location.hostname
  const domains = [...new Set(['', host, host === '' ? '' : `.${host}`])]

  for (const name of METRIKA_COOKIES) {
    for (const domain of domains) {
      const suffix = domain === '' ? '' : `; domain=${domain}`
      document.cookie = `${name}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:01 GMT${suffix}`
    }
  }
}

/** Выключить счётчик после отзыва согласия — в той же сессии, без перезагрузки. */
function stop(): void {
  if (typeof document === 'undefined') return

  // Удалить тег недостаточно: загруженный счётчик живёт в памяти вкладки и
  // после этого продолжает писать вебвизором. `destruct` — штатный способ
  // Метрики выключить его совсем, и вызывать его нужно ДО удаления скрипта.
  const id = metrikaId()
  if (started && id !== null && typeof window.ym === 'function') {
    window.ym(id, 'destruct')
  }

  started = false
  document.getElementById(SCRIPT_ID)?.remove()
  delete window.ym
  clearMetrikaCookies()
}

/**
 * Подключить аналитику к тумблеру согласия. Возвращает функцию отписки —
 * тестам нужно уметь снять слушателя, иначе он утечёт в соседний кейс.
 */
export function setupAnalytics(): () => void {
  const id = metrikaId()
  if (id === null) {
    // Номера счётчика нет — грузить нечего. Молчим: это штатная разработка,
    // а не поломка.
    return () => {}
  }

  if (!needsConsent()) {
    start(id)
    return () => {}
  }

  if (hasConsent('analytics')) start(id)

  return onConsentChange((state) => {
    if (state.analytics) start(id)
    else stop()
  })
}

/**
 * Отправить событие воронки. Без согласия и без счётчика — тихий no-op:
 * вызывающая сторона не обязана каждый раз спрашивать, включена ли аналитика.
 */
export function trackEvent(event: AnalyticsEvent, params?: Record<string, string | number>): void {
  const id = metrikaId()
  if (id === null || !started || typeof window.ym !== 'function') return

  window.ym(id, 'reachGoal', event, params)
}

/** Только для тестов: сбросить состояние загрузки между кейсами. */
export function resetAnalyticsForTests(): void {
  stop()
}
